import reactLazyWithRetry from '@fatso83/retry-dynamic-import/react-lazy'
import { Suspense } from 'react'
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useLocation,
  useRoutes,
} from 'react-router'

import Loading from './components/Loading'
import { Providers, PublicProviders } from './contexts/Providers'
import { Permission, PermissionToPath } from './enums/permission.enum'
import useAuth from './hooks/useAuth'
import { NotFound } from './pages/404'
import { Error500Page } from './pages/500'
import { PageLoading } from './pages/app/loading'
import { ForgotPassword } from './pages/auth/forgot-password'
import NewPasswordPage from './pages/auth/new-password'
import { SignIn } from './pages/auth/sign-in'
import { Error } from './pages/error'
import { ExpiredLink } from './pages/expired-link'
import { Machine3ds } from './pages/machine-3ds'

const AppLayout = reactLazyWithRetry(() =>
  import('./pages/_layouts/app').then((module) => ({
    default: module.AppLayout,
  }))
)

// const AppLayout = lazy(() =>
//   import('./pages/_layouts/app').then((module) => ({
//     default: module.AppLayout,
//   }))
// )
const AppNoMenuLayout = reactLazyWithRetry(() =>
  import('./pages/_layouts/app-no-menu').then((module) => ({
    default: module.AppNoMenuLayout,
  }))
)
const AuthLayout = reactLazyWithRetry(() =>
  import('./pages/_layouts/auth').then((module) => ({
    default: module.AuthLayout,
  }))
)
const LiveOrders = reactLazyWithRetry(() =>
  import('./pages/app/live-orders').then((module) => ({
    default: module.LiveOrders,
  }))
)

const Shopper = reactLazyWithRetry(() =>
  import('./pages/app-no-menu/shopper').then((module) => ({
    default: module.Shopper,
  }))
)
const Dashboard = reactLazyWithRetry(() =>
  import('./pages/app/dashboard').then((module) => ({
    default: module.Dashboard,
  }))
)
const History = reactLazyWithRetry(() =>
  import('./pages/app/orders').then((module) => ({
    default: module.History,
  }))
)
const MenuMaker = reactLazyWithRetry(() =>
  import('./pages/app/menu-maker').then((module) => ({
    default: module.MenuMaker,
  }))
)
const SuperPartner = reactLazyWithRetry(() =>
  import('./pages/app/super-partner').then((module) => ({
    default: module.SuperPartner,
  }))
)
const Settings = reactLazyWithRetry(() =>
  import('./pages/app/settings').then((module) => ({
    default: module.Settings,
  }))
)

interface ProtectedRouteProps {
  permission: string
  children: React.ReactNode
}

const ProtectedRoute = ({ permission, children }: ProtectedRouteProps) => {
  const { user, isLoading } = useAuth()
  const isAllowed = user && !isLoading && user.permissions.includes(permission)

  if (!user && !isLoading) {
    return <Navigate to="/sign-in" replace />
  }

  if (!isAllowed) {
    return (
      <Navigate
        to={PermissionToPath[user?.permissionsOrdered[0] || '']}
        replace
      />
    )
  }

  return children
}

const ProtectedRouteShopper = ({
  permission,
  children,
}: ProtectedRouteProps) => {
  const { user, isLoading, merchantSelected } = useAuth()
  const isAllowed = user && !isLoading && user.permissions.includes(permission)

  if (!user && !isLoading) {
    return <Navigate to="/sign-in" replace />
  }

  if (!merchantSelected?.isPersonalShopper) {
    return <Navigate to="/" replace />
  }

  if (!isAllowed) {
    return (
      <Navigate
        to={PermissionToPath[user?.permissionsOrdered[0] || '']}
        replace
      />
    )
  }

  return children
}

const RedirectSignIn = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading } = useAuth()

  if (user && !isLoading) {
    return <Navigate to={PermissionToPath[user?.permissionsOrdered[0] || '']} />
  }

  return children
}

const MenuMakerInitialRedirect = () => {
  const { merchantSelected } = useAuth()
  const location = useLocation()

  const isPersonalShopper = merchantSelected?.isPersonalShopper

  if (location.pathname === '/menu-maker/') {
    if (isPersonalShopper) {
      return <Navigate to="menus" replace />
    }
    return <Navigate to="overview" replace />
  }

  return <MenuMaker activeTab={0} />
}

const MenuMakerHandler = () => {
  return <Outlet />
}

const MenuMakerRoutesManager = () => {
  const defaultRoutes = [
    {
      index: true,
      element: <MenuMakerInitialRedirect />,
    },
    {
      path: 'overview',
      element: <MenuMaker activeTab={0} />,
    },
    {
      path: 'menus',
      element: <MenuMaker activeTab={1} />,
    },
    {
      path: 'categories',
      element: <MenuMaker activeTab={2} />,
    },
    {
      path: 'items',
      element: <MenuMaker activeTab={3} />,
    },
    {
      path: 'modifiers',
      element: <MenuMaker activeTab={4} />,
    },
  ]

  const element = useRoutes(defaultRoutes)

  return <>{element}</>
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <Providers>
        <Suspense fallback={<Loading size="2.5rem" />}>
          <AppLayout />
        </Suspense>
      </Providers>
    ),
    errorElement: <Error />,
    children: [
      {
        path: '/',
        element: (
          <ProtectedRoute permission={Permission.LIVE_ORDERS}>
            <Suspense fallback={<PageLoading />}>
              <LiveOrders />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: '/dashboard',
        element: (
          <ProtectedRoute permission={Permission.DASHBOARD}>
            <Suspense fallback={<PageLoading />}>
              <Dashboard />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: '/orders',
        element: (
          <ProtectedRoute permission={Permission.ORDERS}>
            <Suspense fallback={<PageLoading />}>
              <History />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: '/menu-maker',
        element: (
          <ProtectedRoute permission={Permission.MENU_MAKER}>
            <Suspense fallback={<PageLoading />}>
              <MenuMakerHandler />
            </Suspense>
          </ProtectedRoute>
        ),
        children: [
          {
            path: '*',
            element: <MenuMakerRoutesManager />,
          },
        ],
      },
      {
        path: '/tutorials/:videoSlug',
        element: (
          <ProtectedRoute
            permission={Permission.LIVE_ORDERS || Permission.MENU_MAKER}
          >
            <MenuMaker activeTab={0} />
          </ProtectedRoute>
        ),
      },
      {
        path: '/',
        children: [
          {
            path: '/super-partner',
            element: <Navigate to="/super-partner/new-campaign" replace />,
          },
          {
            path: '/super-partner/new-campaign',
            element: (
              <ProtectedRoute permission={Permission.SUPER_PARTNER}>
                <Suspense fallback={<PageLoading />}>
                  <SuperPartner activeTab={0} />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: '/super-partner/campaigns',
            element: (
              <ProtectedRoute permission={Permission.SUPER_PARTNER}>
                <Suspense fallback={<PageLoading />}>
                  <SuperPartner activeTab={1} />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: '/super-partner/vouchers',
            element: (
              <ProtectedRoute permission={Permission.SUPER_PARTNER}>
                <Suspense fallback={<PageLoading />}>
                  <SuperPartner activeTab={2} />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: '/super-partner/payment',
            element: (
              <ProtectedRoute permission={Permission.SUPER_PARTNER}>
                <Suspense fallback={<PageLoading />}>
                  <SuperPartner activeTab={3} />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: '/settings',
        element: (
          <ProtectedRoute permission={Permission.ADMIN}>
            <Suspense fallback={<PageLoading />}>
              <Settings />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin-settings',
        element: (
          <ProtectedRoute permission={Permission.SUPER_ADMIN}>
            <Suspense fallback={<PageLoading />}>
              <Settings admin />
            </Suspense>
          </ProtectedRoute>
        ),
      },
    ],
  },
  {
    path: '/',
    element: (
      <Providers>
        <Suspense fallback={<Loading size="2.5rem" />}>
          <AppNoMenuLayout />
        </Suspense>
      </Providers>
    ),
    errorElement: <Error />,
    children: [
      {
        path: '/shopper/:id',
        element: (
          <ProtectedRouteShopper permission={Permission.LIVE_ORDERS}>
            <Suspense fallback={<PageLoading />}>
              <Shopper />
            </Suspense>
          </ProtectedRouteShopper>
        ),
      },
    ],
  },
  {
    path: '/',
    element: (
      <PublicProviders>
        <Suspense fallback={<Loading size="2.5rem" />}>
          <AppNoMenuLayout />
        </Suspense>
      </PublicProviders>
    ),
    errorElement: <Error />,
    children: [
      {
        path: '/public-shopper/:id',
        element: (
          <Suspense fallback={<PageLoading />}>
            <Shopper />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: '/',
    element: (
      <Providers>
        <RedirectSignIn>
          <Suspense fallback={<Loading size="2.5rem" />}>
            <AuthLayout />
          </Suspense>
        </RedirectSignIn>
      </Providers>
    ),
    errorElement: <Error />,
    children: [
      { path: '/sign-in', element: <SignIn /> },
      { path: '/forgot-password', element: <ForgotPassword /> },
      { path: '/new-password/*', element: <NewPasswordPage /> },
    ],
  },
  {
    path: '/expired-link',
    element: (
      <Providers>
        <ExpiredLink />
      </Providers>
    ),
    errorElement: <Error />,
  },
  {
    path: '/500',
    errorElement: <Error />,
    element: <Error500Page />,
  },
  {
    path: '/machine-3ds',
    errorElement: <Error />,
    element: <Machine3ds />,
  },
  {
    path: '*',
    element: <NotFound />,
  },
])
